import React from 'react';

/*
 * Brand color reference - matches --color-* CSS variables in App.css and COLORS in constants.js.
 * Components in this file use Tailwind arbitrary values (e.g. text-[#2F5233]) directly
 * for build-time optimization. This comment documents the intended color mapping:
 *
 *   focusBorder: '#52A675'  // --color-primary (used in focus:border-[#52A675])
 *   text:        '#2F5233'  // --color-dark (used in text-[#2F5233])
 */

/**
 * Form label component with consistent styling.
 * 
 * @param {string} children - Label text
 * @param {boolean} required - Show red asterisk for required fields
 * @param {string} htmlFor - ID of the associated form element
 * @param {string} className - Additional CSS classes
 */
export const FormLabel = ({ children, required, htmlFor, className = '' }) => (
  <label 
    htmlFor={htmlFor}
    className={`block text-sm font-semibold text-[#2F5233] mb-2 ${className}`}
  >
    {children}
    {required && <span className="inline-block w-1.5 h-1.5 bg-red-500 rounded-full ml-0.5 align-middle"></span>}
  </label>
);

/**
 * Text input component with consistent styling.
 * 
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Show required indicator
 * @param {string} error - Error message to display
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the input element
 */
export const FormInput = ({ label, required, error, wrapperClassName = '', ...props }) => (
  <div className={wrapperClassName}>
    {label && <FormLabel required={required} htmlFor={props.id}>{label}</FormLabel>}
    <input
      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
      {...props}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Date input component with mobile-friendly styling.
 * Wrapped in overflow container to prevent mobile display issues.
 * 
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Show required indicator
 * @param {string} error - Error message to display
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the input element
 */
export const FormDateInput = ({ label, required, error, wrapperClassName = '', onKeyDown, ...props }) => (
  <div className={wrapperClassName}>
    {label && <FormLabel required={required} htmlFor={props.id}>{label}</FormLabel>}
    <div className="overflow-hidden">
      <input
        type="date"
        className="w-full min-w-0 max-w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] appearance-none bg-white"
        onKeyDown={(e) => {
          e.stopPropagation();
          onKeyDown?.(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        {...props}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Time input component with mobile-friendly styling.
 * 
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Show required indicator
 * @param {string} error - Error message to display
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the input element
 */
export const FormTimeInput = ({ label, required, error, wrapperClassName = '', ...props }) => (
  <div className={wrapperClassName}>
    {label && <FormLabel required={required} htmlFor={props.id}>{label}</FormLabel>}
    <div className="overflow-hidden">
      <input
        type="time"
        className="w-full min-w-0 max-w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] appearance-none bg-white"
        {...props}
      />
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Select dropdown component with consistent styling.
 * 
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Show required indicator
 * @param {string} error - Error message to display
 * @param {Array} options - Array of {value, label} objects for options
 * @param {string} placeholder - Placeholder text for empty option
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the select element
 */
export const FormSelect = ({ 
  label, 
  required, 
  error, 
  options = [], 
  placeholder,
  wrapperClassName = '', 
  ...props 
}) => (
  <div className={wrapperClassName}>
    {label && <FormLabel required={required} htmlFor={props.id}>{label}</FormLabel>}
    <select
      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white"
      {...props}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Textarea component with consistent styling.
 * 
 * @param {string} label - Label text (optional)
 * @param {boolean} required - Show required indicator
 * @param {string} error - Error message to display
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the textarea element
 */
export const FormTextarea = ({ label, required, error, wrapperClassName = '', ...props }) => (
  <div className={wrapperClassName}>
    {label && <FormLabel required={required} htmlFor={props.id}>{label}</FormLabel>}
    <textarea
      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white resize-y"
      {...props}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

/**
 * Checkbox component with label.
 * 
 * @param {string} label - Label text
 * @param {boolean} checked - Checkbox state
 * @param {function} onChange - Change handler
 * @param {string} wrapperClassName - Additional classes for the wrapper div
 * @param {object} props - All other props passed to the input element
 */
export const FormCheckbox = ({ label, checked, onChange, wrapperClassName = '', ...props }) => (
  <label className={`flex items-center gap-2 cursor-pointer ${wrapperClassName}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="w-4 h-4 rounded border-gray-300 text-[#52A675] focus:ring-[#52A675]"
      {...props}
    />
    <span className="text-sm text-[#2F5233]">{label}</span>
  </label>
);
